/**
 * SAM (§1.7): interceptor resolution and engagement for land SAMs and carrier SAMs. A SAM engages
 * hostile ICBMs / SLBMs / airborne aircraft within range that its team sees (radar cueing) or that
 * are inside its own 4 mu sensor, one engagement at a time: shoot-look-shoot, one interceptor, and
 * another once that one has resolved. Land SAMs radiate when they fire: every player sees the
 * site for `sam.exposedTicks` after its last shot. A SILENT site (emissions
 * control) has no own sensor, engages only cued targets at half range, and is never exposed.
 */
import { BALANCE } from '../balance.js';
import { addEntity, allied, isAircraft, isAirborne, removeEntity, shipActive, structureActive } from '../entities.js';
import { nextFloat } from '../rng.js';
import type { CarrierEntity, Entity, InterceptorEntity, MatchState, SamEntity, SimEvent } from '../types.js';

const S = BALANCE.sam;

/** A land SAM's engagement range (mu): halved while silent (emissions control). */
export function samRange(silent: boolean): number {
  return S.range * (silent ? S.silentRangeMult : 1);
}

export function samSystem(state: MatchState, events: SimEvent[]): void {
  resolveInterceptors(state, events);
  engage(state, events);
}

function resolveInterceptors(state: MatchState, events: SimEvent[]): void {
  const due: InterceptorEntity[] = [];
  for (const e of state.entities.values()) if (e.kind === 'interceptor' && e.resolveTick <= state.tick) due.push(e);
  for (const i of due) {
    const target = state.entities.get(i.targetId);
    const hit = !!target && nextFloat(state.combatRng) < i.pk;
    events.push({
      type: 'interceptResolve',
      shooterId: i.shooterId,
      shooterOwner: i.owner,
      targetId: i.targetId,
      fireTick: i.fireTick,
      resolveTick: i.resolveTick,
      hit,
      ...(target ? { where: { sx: i.ox, sy: i.oy, tx: target.x, ty: target.y } } : {}),
    });
    const shooter = state.entities.get(i.shooterId);
    if (shooter?.kind === 'sam' && shooter.engaging === i.id) shooter.engaging = 0;
    if (shooter?.kind === 'carrier' && shooter.samEngaging === i.id) shooter.samEngaging = 0;
    removeEntity(state, i.id, 'detonated', events);
    if (hit && target) {
      if ((target.kind === 'icbm' || target.kind === 'slbm') && target.owner > 0) state.stats.players[target.owner - 1].intercepted++;
      removeEntity(state, target.id, 'intercepted', events);
    }
  }
}

function pkAgainst(kind: Entity['kind']): number {
  switch (kind) {
    case 'icbm':
      return S.pk.icbm;
    case 'slbm':
      return S.pk.slbm;
    case 'bomber':
      return S.pk.bomber;
    case 'fighter':
      return S.pk.fighter;
    default:
      return 0;
  }
}

/**
 * Engagement priority: the SAM's player-assigned priority missile (samPriority intent), then
 * missiles by earliest impact, then bombers, then fighters.
 */
function priority(e: Entity): number {
  if (e.kind === 'icbm' || e.kind === 'slbm') return e.launchTick + e.flightTicks;
  return e.kind === 'bomber' ? 1e9 : 2e9;
}

function engage(state: MatchState, events: SimEvent[]): void {
  const tick = state.tick;
  const threats: Entity[] = [];
  for (const e of state.entities.values()) {
    if (e.kind === 'icbm' || e.kind === 'slbm') {
      if (e.launchTick + e.flightTicks > tick + S.interceptorTicks) threats.push(e);
    } else if (isAircraft(e) && isAirborne(e)) threats.push(e);
  }
  if (!threats.length) {
    reloadAll(state);
    return;
  }
  // targets already engaged, per shooter-owner bit
  const engaged = new Map<number, number>();
  for (const e of state.entities.values()) {
    if (e.kind === 'interceptor') engaged.set(e.targetId, (engaged.get(e.targetId) ?? 0) | (1 << e.owner));
  }
  for (const e of state.entities.values()) {
    if (e.kind === 'sam') {
      if (structureActive(state, e) && e.ammo > 0 && e.engaging === 0) fire(state, e, threats, engaged, events);
    } else if (e.kind === 'carrier') {
      if (shipActive(state, e) && e.samAmmo > 0 && e.samEngaging === 0) fire(state, e, threats, engaged, events);
    }
  }
  reloadAll(state);
}

function fire(state: MatchState, shooter: SamEntity | CarrierEntity, threats: Entity[], engaged: Map<number, number>, events: SimEvent[]): void {
  const isSam = shooter.kind === 'sam';
  const silent = isSam && shooter.silent;
  const range = isSam ? samRange(silent) : BALANCE.carrier.samRange;
  const r2 = range * range;
  const bomber2 = Math.min(range, S.bomberRange) ** 2;
  // a silent site has no sensor of its own: cued targets only
  const own2 = silent ? -1 : S.sensor * S.sensor;
  const team = state.vision.teamMask[shooter.owner];
  let best: Entity | null = null;
  let bestP = Infinity;
  let bestD = Infinity;
  const pinned = isSam ? shooter.priorityId : 0;
  if (pinned && !state.entities.has(pinned)) (shooter as SamEntity).priorityId = 0;
  for (const t of threats) {
    if (t.owner === shooter.owner || allied(state, shooter.owner, t.owner)) continue;
    if ((engaged.get(t.id) ?? 0) & team) continue;
    const d = (t.x - shooter.x) ** 2 + (t.y - shooter.y) ** 2;
    if (d > (t.kind === 'bomber' ? bomber2 : r2)) continue;
    const cued = ((t.visMask >> shooter.owner) & 1) === 1;
    if (!cued && !(isSam && d <= own2)) continue;
    const p = t.id === pinned ? -1 : priority(t);
    if (p < bestP || (p === bestP && (d < bestD || (d === bestD && best !== null && t.id < best.id)))) {
      best = t;
      bestP = p;
      bestD = d;
    }
  }
  if (!best) return;
  const fireTick = state.tick;
  const resolveTick = fireTick + S.interceptorTicks;
  const pk = isSam ? pkAgainst(best.kind) : BALANCE.carrier.samPk;
  const target = best;
  const i = addEntity<InterceptorEntity>(state, (id) => ({
    id,
    kind: 'interceptor',
    owner: shooter.owner,
    x: shooter.x,
    y: shooter.y,
    createdTick: fireTick,
    visMask: 0,
    shooterId: shooter.id,
    targetId: target.id,
    ox: shooter.x,
    oy: shooter.y,
    fireTick,
    resolveTick,
    pk,
  }));
  if (isSam) {
    shooter.ammo--;
    shooter.engaging = i.id;
  } else {
    shooter.samAmmo--;
    shooter.samEngaging = i.id;
  }
  events.push({ type: 'interceptFire', shooterId: shooter.id, shooterOwner: shooter.owner, targetId: target.id, fireTick, resolveTick, where: { sx: shooter.x, sy: shooter.y, tx: target.x, ty: target.y } });
  engaged.set(target.id, (engaged.get(target.id) ?? 0) | (1 << shooter.owner));
  // its engagement radar radiated: the site is seen by everyone for a while (unless silent)
  if (isSam && !silent) shooter.exposedUntil = fireTick + S.exposedTicks;
}

function reloadAll(state: MatchState): void {
  const tick = state.tick;
  for (const e of state.entities.values()) {
    if (e.kind === 'sam') {
      if (e.ammo >= S.ammo) e.reloadAt = 0;
      else if (e.reloadAt === 0) e.reloadAt = tick + S.reloadTicks;
      else if (tick >= e.reloadAt) {
        e.ammo++;
        e.reloadAt = e.ammo < S.ammo ? tick + S.reloadTicks : 0;
      }
    } else if (e.kind === 'carrier') {
      const max = BALANCE.carrier.samAmmo;
      if (e.samAmmo >= max) e.samReloadAt = 0;
      else if (e.samReloadAt === 0) e.samReloadAt = tick + BALANCE.carrier.samReloadTicks;
      else if (tick >= e.samReloadAt) {
        e.samAmmo++;
        e.samReloadAt = e.samAmmo < max ? tick + BALANCE.carrier.samReloadTicks : 0;
      }
    }
  }
}
